/**
 * Query Key Factory
 * =================
 * Centralised, hierarchical query keys for React Query. Every domain area gets
 * its own namespace so cache invalidation can be scoped precisely:
 *
 *   queryKeys.devices.all       → ['devices']              — invalidate everything
 *   queryKeys.devices.lists()   → ['devices', 'list']      — invalidate all lists
 *   queryKeys.devices.list(f)   → ['devices', 'list', f]   — one filtered list
 *   queryKeys.devices.detail(id)→ ['devices', 'detail', id]— one entity
 *
 * Convention for future migration slices:
 *   1. Add a new namespace here when migrating a domain area.
 *   2. Always use these keys in useQuery/useInfiniteQuery — never inline strings.
 *   3. Use `.all` for broad invalidation after mutations.
 *   4. Use `.lists()` (no args) to invalidate every list variant at once.
 */

// ---------------------------------------------------------------------------
// Helper: builds the standard { all, lists, list, details, detail } shape
// for a given domain scope array.
// ---------------------------------------------------------------------------
function createKeys<TScope extends readonly string[]>(scope: TScope) {
  return {
    /** Broadest key — matches everything in this domain. */
    all: [...scope] as const,
    /** Matches every list query (regardless of filters). */
    lists: () => [...scope, 'list'] as const,
    /** One specific list query, discriminated by its filter object. */
    list: (filters?: unknown) => [...scope, 'list', filters] as const,
    /** Matches every detail query. */
    details: () => [...scope, 'detail'] as const,
    /** One specific detail query by id. */
    detail: (id: string) => [...scope, 'detail', id] as const,
  };
}

// ---------------------------------------------------------------------------
// Domain namespaces
// ---------------------------------------------------------------------------
export const queryKeys = {
  /** Azure subscriptions */
  subscriptions: createKeys(['subscriptions']),

  /** Azure resource groups */
  resourceGroups: createKeys(['resourceGroups']),

  /**
   * Portal resources (the combined sites + device-register fetch).
   * The existing `useSitesAndDeviceRegisters` hook lives here.
   */
  resources: createKeys(['resources']),

  /** Sites (Cozify.Sites provider) */
  sites: createKeys(['sites']),

  /** Devices within a single site */
  devices: createKeys(['devices']),

  /** Hubs */
  hubs: createKeys(['hubs']),

  /** Dashboards */
  dashboards: createKeys(['dashboards']),

  /** Cross-tenant device listing (the infinite-scroll list) */
  tenantDevices: createKeys(['tenantDevices']),

  /** Egress targets (per-site) */
  egress: createKeys(['egress']),

  /** Tenants (active tenant, tenant list, locations) */
  tenants: createKeys(['tenants']),

  /** Device configuration templates (per device-register and type) */
  deviceTemplates: createKeys(['deviceTemplates']),

  /** Device configuration (per device) */
  deviceConfiguration: createKeys(['deviceConfiguration']),

  /** Key vault (tenant- or deviceRegister-scoped) */
  keyVault: {
    ...createKeys(['keyVault']),
    /**
     * Scoped keys for key vault queries that operate within a specific scope
     * (tenant or deviceRegister). Mirrors the old per-scope key structure.
     */
    scoped: (scope: 'tenant' | 'deviceRegister') => ({
      all: ['keyVault', scope] as const,
      lists: () => ['keyVault', scope, 'list'] as const,
      list: (params?: unknown) => ['keyVault', scope, 'list', params] as const,
      details: () => ['keyVault', scope, 'detail'] as const,
      detail: (keyId: string, extra?: unknown) =>
        ['keyVault', scope, 'detail', keyId, extra] as const,
    }),
  },
} as const;
